'use client'

import React from 'react'
import { useLanguage } from '@/app/(frontend)/providers/LanguageProvider'
import { motion } from "framer-motion";

const comparisonData = [
  {
    feature: 'Regulatory Compliance',
    arFeature: 'التوافق التنظيمي',
    ccsm: 'Fully licensed, government-approved inland container depot',
    arCcsm: 'مرخص بالكامل ومعتمد من الحكومة كمستودع حاويات داخلي',
    typical: 'Basic licenses, limited bonded approvals',
    arTypical: 'ترخيص أساسي؛ موافقات محدودة للتخزين المؤقت',
  },
  {
    feature: 'Strategic Location',
    arFeature: 'الموقع الاستراتيجي',
    ccsm: '12 km from Hamad Port; near Mesaieed & Industrial City',
    arCcsm: 'على بُعد 12 كم من ميناء حمد؛ بالقرب من مسيعيد ومدينة الصناعات',
    typical: 'Often outside key logistics zones',
    arTypical: 'غالبًا خارج المناطق اللوجستية الرئيسية',
  },
  {
    feature: 'Storage Types Offered',
    arFeature: 'أنواع التخزين المتاحة',
    ccsm: 'Self-storage, container storage, break bulk, reefer, oversized',
    arCcsm: 'تخزين ذاتي، تخزين حاويات، شحن مفكك، حاويات مبردة، حاويات ضخمة',
    typical: 'Basic indoor or container storage only',
    arTypical: 'تخزين داخلي أو حاويات فقط',
  },
  {
    feature: 'Maintenance & Repairs',
    arFeature: 'الصيانة والإصلاحات',
    ccsm: 'On-site certified servicing, heat treatment, painting, carpentry',
    arCcsm: 'خدمة معتمدة في الموقع، معالجة حرارية، طلاء، أعمال نجارة',
    typical: 'Outsourced or not offered',
    arTypical: 'خدمات خارجية أو غير متوفرة',
  },
  {
    feature: 'Infrastructure Size',
    arFeature: 'حجم البنية التحتية',
    ccsm: '265,000 sqm yard + 12,000 sqm self-storage',
    arCcsm: '265,000 متر مربع ساحة + 12,000 متر مربع تخزين ذاتي',
    typical: 'Smaller facilities with limited expansion',
    arTypical: 'مرافق أصغر مع توسع محدود',
  },
  {
    feature: 'Security & Monitoring',
    arFeature: 'الأمن والمراقبة',
    ccsm: '24/7 live surveillance, gated access, activity logs',
    arCcsm: 'مراقبة حية على مدار الساعة، وصول محكوم، سجلات النشاطات',
    typical: 'Standard locks or low-surveillance systems',
    arTypical: 'أقفال قياسية أو أنظمة مراقبة منخفضة',
  },
  {
    feature: 'Technology Integration',
    arFeature: 'تكامل التكنولوجيا',
    ccsm: 'Container tracking, inventory management, climate control',
    arCcsm: 'تتبع الحاويات، إدارة المخزون، تحكم في المناخ',
    typical: 'Manual system, no tracking',
    arTypical: 'أنظمة يدوية، بدون تتبع',
  },
  {
    feature: 'Container Sales & Leasing',
    arFeature: 'بيع وتأجير الحاويات',
    ccsm: 'New/used sales/leasing, modifications, reefer & tank containers',
    arCcsm: 'بيع حاويات جديدة/مستعملة، تأجير، تعديلات، حاويات مبردة وسائلة',
    typical: 'Limited to dry containers only',
    arTypical: 'مقتصر على الحاويات الجافة فقط',
  },
  {
    feature: 'On-Site Business Amenities',
    arFeature: 'المرافق التجارية في الموقع',
    ccsm: 'Offices, accommodations, retail shops within logistics hub',
    arCcsm: 'مكاتب، سكن، محلات تجزئة داخل مركز اللوجستيات',
    typical: 'None or offsite at extra cost',
    arTypical: 'لا شيء أو خارج الموقع بتكلفة إضافية',
  },
  {
    feature: 'Service Customization',
    arFeature: 'تخصيص الخدمة',
    ccsm: 'Tailored packages for SMEs, expats, corporates, and government',
    arCcsm: 'حزم مخصصة للشركات الصغيرة والمتوسطة، المغتربين، الشركات الكبرى، والحكومة',
    typical: 'One-size-fits-all solutions',
    arTypical: 'حلول غير مخصصة',
  },
  {
    feature: 'Customer Support',
    arFeature: 'دعم العملاء',
    ccsm: 'Dedicated team, fast response, transparent pricing',
    arCcsm: 'فريق مخصص، استجابة سريعة، تسعير شفاف',
    typical: 'Slow or impersonal service',
    arTypical: 'خدمة بطيئة أو غير شخصية',
  },
  {
    feature: 'Part Of Logistics Network',
    arFeature: 'جزء من شبكة اللوجستيات',
    ccsm: 'Backed by Aero Group & CTC with 40+ years of expertise',
    arCcsm: 'مدعوم من مجموعة أيرو وCTC بخبرة تزيد عن 40 عامًا',
    typical: 'Independent or new market entrants',
    arTypical: 'شركات مستقلة أو جديدة في السوق',
  },
]

const ComparisonTable = () => {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  return (
    <section
      className={`py-12 px-4 sm:px-6 lg:px-16 2xl:px-[4.5rem] xl:pb-44 bg-white -mt-3 ${isArabic ? 'text-right' : 'text-left'}`}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="md:max-w-2xl lg:max-w-5xl xl:max-w-[1250px] 2xl:max-w-[1400px] mx-auto">
        <motion.h2
  initial={{ opacity: 0, y: 40 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.4, ease: "easeOut" }}
  viewport={{ once: true }}
  className={`why-heading text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] font-[500] mb-3 md:mb-8 mt-10 ${
    isArabic ? "text-right" : "text-left md:text-center"
  } text-[#414141]`}
>
  {isArabic
    ? "كيف تتفوق Container City® على مزودي التخزين التقليديين"
    : "How Container City® Stacks Up Against Typical Storage Providers"}
</motion.h2>

<motion.p
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
  viewport={{ once: true }}
  className={`${
    isArabic ? "text-right md:text-center" : "text-left md:text-center"
  } text-[#666666] mb-14 font-normal text-[15px] md:text-xs xl:text-base`}
>
  {isArabic
    ? "شوف الفرق. واخذ القرار بثقة."
    : "See the difference. Feel the confidence."}
</motion.p>


        {/* Desktop view */}
        <div className="hidden md:block mb-32">
          {/* Header row */}
          <div className="grid grid-cols-[1.2fr_1.5fr_1.3fr] text-sm gap-2">
            <div className="font-normal xl:text-3xl py-4 pr-4 text-[#ee3e25] max-w-[350px]">
              {isArabic ? 'الميزة / الخدمة' : 'Feature/Service'}
            </div>
            <div className="font-normal  xl:text-3xl py-4 px-2 text-[#ee3e25] max-w-[400px]">
              {isArabic ? 'Container City® (مدينة الحاويات قطر)' : 'Container City® Qatar'}
            </div>
            <div className="font-normal  xl:text-3xl py-4 px-2 text-[#ee3e25] max-w-[280px]">
              {isArabic ? 'المزود العادي في قطر' : 'Typical Provider in Qatar'}
            </div>
          </div>

          {/* Data rows */}
          {comparisonData.map((row, index) => (
            <div key={index} className="grid grid-cols-[1.2fr_1.5fr_1.3fr] border-b border-gray-400">
              <div className="py-6 text-black xl:text-xl max-w-[160px]">
                {isArabic ? row.arFeature : row.feature}
              </div>
              <div className="py-6 px-4 text-[#777777] md:text-[12px] xl:text-base max-w-[350px]">
                {isArabic ? row.arCcsm : row.ccsm}
              </div>
              <div className="py-6 px-4 text-[#777777] md:text-[12px] xl:text-base max-w-[300px]">
                {isArabic ? row.arTypical : row.typical}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile view */}
        <div className="md:hidden space-y-8">
          {comparisonData.map((row, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-[18px] md:text-base font-semibold text-red-500 mb-2">
                {isArabic ? row.arFeature : row.feature}
              </h3>
              <div className="mb-2">
                <p className="text-[15px] md:text-sm text-gray-700 font-semibold">
                  {isArabic ? 'Container City® (مدينة الحاويات قطر)' : 'Container City® Qatar:'}
                </p>
                <p className="text-[15px] md:text-sm text-gray-600">{isArabic ? row.arCcsm : row.ccsm}</p>
              </div>
              <div>
                <p className="text-[15px] md:text-sm text-gray-700 font-semibold">
                  {isArabic ? 'المزود العادي في قطر:' : 'Typical Provider in Qatar:'}
                </p>
                <p className="text-[15px] md:text-sm text-gray-600">{isArabic ? row.arTypical : row.typical}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ComparisonTable
